import React, { useEffect, useRef, useState } from 'react';
import { Button } from 'react-bootstrap';
import L from 'leaflet';
import empowermentZones from './../data/empowermentZones.json';

const SingleAddressChecker = () => {
  const mapRef = useRef(null);
  const geojsonLayerRef = useRef(null);
  const markerRef = useRef(null); // Add marker reference
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResultMsg, setSearchResultMsg] = useState('');
  const [isLocationFound, setIsLocationFound] = useState(null);

  useEffect(() => {
    console.log("Initialize map");

    // Initialize map
    const map = L.map(mapRef.current).setView([38.5, -96.5], 4);

    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      maxZoom: 18,
      attribution: 'Map data © OpenStreetMap contributors',
    }).addTo(map);

    // Add GeoJSON Layer
    const geojsonLayer = L.geoJSON(empowermentZones, {
      style: {
        color: 'blue',
        fillOpacity: 0.3,
      },
      onEachFeature: (feature, layer) => {
        const name = feature.properties.NAME || 'Unnamed';
        const state = feature.properties.STATEABBR || '';
        layer.bindPopup(`<strong>${name}</strong><br>State: ${state}`);
      },
    }).addTo(map);

    geojsonLayerRef.current = geojsonLayer;
    map.fitBounds(geojsonLayer.getBounds());

    const ac = document.getElementById("autocompleteResults");
    console.log(ac);
    document.addEventListener("click", (e) => {
      if (e.target.id !== "searchInput") {
        if (ac) {
          ac.style.display = "none";
        }
      }
    });

    // Return cleanup function to remove map
    return () => {
      map.remove();
    };
  }, []);

  const isPointInPolygon = (point, polygon) => {
    const [x, y] = point;
    let inside = false;
    for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
      const [xi, yi] = polygon[i];
      const [xj, yj] = polygon[j];
      const intersect =
        (yi > y !== yj > y) &&
        x < ((xj - xi) * (y - yi)) / (yj - yi || 0.000001) + xi;
      if (intersect) inside = !inside;
    }
    return inside;
  };

  const searchLocation = async () => {
    if (!searchQuery) return alert('Please enter a search term.');

    try {
      const res = await fetch(
        `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(searchQuery)}`
      );
      const data = await res.json();
      if (!data || !data.length) return alert('Location not found.');

      const lat = parseFloat(data[0].lat);
      const lon = parseFloat(data[0].lon);
      const map = geojsonLayerRef.current._map;
      map.setView([lat, lon], 13);

      // Add or update marker
      if (markerRef.current) {
        map.removeLayer(markerRef.current); // Remove previous marker
      }
      markerRef.current = L.marker([lat, lon]).addTo(map); // Add new marker

      const point = [lon, lat];
      let found = false;

      geojsonLayerRef.current.eachLayer((layer) => {
        const geom = layer.feature.geometry;
        if (geom.type === "Polygon") {
          if (isPointInPolygon(point, geom.coordinates[0])) {
            layer.setStyle({ color: "red" });
            layer.openPopup();
            found = true;
          }
        } else if (geom.type === "MultiPolygon") {
          for (const poly of geom.coordinates) {
            if (isPointInPolygon(point, poly[0])) {
              layer.setStyle({ color: "red" });
              layer.openPopup();
              found = true;
              break;
            }
          }
        }
      });

      setIsLocationFound(found);
      setSearchResultMsg(
        found
          ? '✅ This location IS in an Empowerment Zone!'
          : '❌ This location is NOT in an Empowerment Zone.'
      );
    } catch (err) {
      alert('Geocoding failed.');
    }
  };

  const [timer, setTimer] = useState(null);

  const handleInputChange = (e) => {
    setSearchQuery(e.target.value);
    const query = e.target.value;
    const ac = document.getElementById("autocompleteResults");
    if (query.length < 3) {
      ac.style.display = "none";
      return;
    }

    // Clear the previous timer if the user is typing fast
    if (timer) {
      clearTimeout(timer);
    }

    // Set a new timer to call the API after 500ms of no typing
    setTimer(setTimeout(() => fetchLocationData(query), 500));
  };

  const fetchLocationData = async (query) => {
    const ac = document.getElementById("autocompleteResults");
    if (!query) return;
    if (query.length < 3) {
      ac.style.display = "none";
      return;
    }

    try {
      const response = await fetch(`https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(query)}`);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();
      ac.innerHTML = "";
      if (!data.length) {
        ac.style.display = "none";
        return;
      }
      data.forEach(item => {
        const div = document.createElement("div");
        div.textContent = item.display_name;
        div.addEventListener("click", () => {
          document.getElementById("searchInput").value = item.display_name;
          ac.style.display = "none";
        });
        ac.appendChild(div);
      });
      ac.style.display = "block";
    } catch (error) {
      console.error('Fetch error:', error);
    }
  };

  const handleSearchClick = () => {
    searchLocation();
  };

  return (
    <>
      <div className='flex flex row justify-between items-center'>
        <div className="chart-title">Check Address</div>
        <div className="relative">
          <div id="autocompleteResults"></div>
          <div
            id="searchResultMsg"
            style={{
              position: 'absolute',
              top: '70px',
              zIndex: 1000,
              background: 'white',
              padding: '6px 12px',
              borderRadius: '6px',
              fontFamily: 'Arial,sans-serif',
              fontSize: '14px',
              color: '#333',
              display: searchResultMsg ? 'block' : 'none',
              boxShadow: '0 1px 5px rgba(0,0,0,0.2)',
            }}
          >
            {searchResultMsg}
          </div>
          <div className="flex pr-3">
            <input
              id="searchInput"
              placeholder="Search city, address, or ZIP"
              value={searchQuery}
              onChange={handleInputChange}
              className="w-30 md:w-48 h-8 md:h-10 pl-10 pr-4 rounded-xl border-1 border-gray-300  search-input"
              style={{marginBottom: "0px"}}
              type="text"
            />
            <Button variant="primary" onClick={handleSearchClick} className="ml-2"><i className=" text-sm fas fa-search block"></i></Button>
          </div>
        </div>
      </div>
      <section>
        <div
          id="map"
          style={{
            height: '500px',
          }}
          ref={mapRef}
        ></div>
      </section>
    </>
  );
};

export default SingleAddressChecker;